import { BookmarkSimple, Flask, Lightning, Sparkle, Trophy } from '@phosphor-icons/react';
import {
  getGetExperimentQueryKey,
  useGetExperiment,
  useListFlowExperiments,
  useMakeMoreLikeWinner,
  usePromoteWinner,
} from '@reelplicate/api-client/experiments';
import { useStartFlowRun } from '@reelplicate/api-client/flow-runs';
import { useGetFlow } from '@reelplicate/api-client/flows';
import type { ExperimentDto, ExperimentVariantDto } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Badge,
  Button,
  cn,
  EmptyState,
  Inline,
  Select,
  SelectItem,
  Skeleton,
  Stack,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';

import { useFullBleed } from '@/components/layout-mode';
import { formatWhen } from '@/lib/format-when';

import { FlowPageHeader } from '../flow-page-header';

type Metric = 'watchTime' | 'views' | 'saves' | 'shares';

const METRICS: Record<
  Metric,
  { name: string; by: string; format: (n: number) => string }
> = {
  watchTime: {
    name: 'average watch time',
    by: 'Average watch time',
    format: (n) => `${n.toFixed(1)}s`,
  },
  views: { name: 'views', by: 'Views', format: (n) => compact(n) },
  saves: {
    name: 'saves per view',
    by: 'Saves per view',
    format: (n) => `${(n * 100).toFixed(1)}%`,
  },
  shares: {
    name: 'shares per view',
    by: 'Shares per view',
    format: (n) => `${(n * 100).toFixed(1)}%`,
  },
};

const NOUNS: Record<string, [string, string]> = {
  hook: ['hook', 'hooks'],
  visuals: ['visuals', 'visuals'],
  caption: ['caption', 'captions'],
  length: ['length', 'lengths'],
  format: ['format', 'formats'],
};

/** What stays the same while a variable changes (the "Held constant" row). */
const HELD: Record<string, string[]> = {
  hook: ['body', 'visuals', 'caption', 'voice'],
  visuals: ['script', 'caption', 'voice'],
  caption: ['video'],
  length: ['hook', 'visuals', 'caption'],
  format: ['hook', 'visuals', 'caption'],
};

const COMPACT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const compact = (n: number) => COMPACT.format(n);
const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function valueOf(metric: Metric, v: ExperimentVariantDto): number {
  switch (metric) {
    case 'watchTime':
      return v.avgWatchSeconds;
    case 'views':
      return v.views;
    case 'saves':
      return v.views ? v.saves / v.views : 0;
    case 'shares':
      return v.views ? v.shares / v.views : 0;
  }
}

/**
 * `/flows/:flowId/results` - Figma "Experiment results": who won, what was
 * tested, a bar per variant and the variant cards; promote the winner or make
 * more like it.
 */
export function ResultsPage() {
  useFullBleed();
  const { flowId = '' } = useParams();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspaceId) } };
  const { data: flow } = useGetFlow(workspaceId, flowId, enabled);
  const { data: experiments } = useListFlowExperiments(workspaceId, flowId, enabled);
  const [picked, setPicked] = useState<string | null>(null);
  const experimentId = picked ?? experiments?.[0]?.id ?? '';
  const { data: experiment } = useGetExperiment(workspaceId, experimentId, {
    query: { enabled: Boolean(workspaceId && experimentId) },
  });
  const start = useStartFlowRun();
  const navigate = useNavigate();

  const runAgain = async () => {
    try {
      await start.mutateAsync({ id: workspaceId, flowId, data: { mode: 'FULL' } });
    } catch {
      return; // Shown as a toast.
    }
    toast.success('Run started');
    void navigate(`/flows/${flowId}`);
  };

  const chip = experiment && <StatusChip experiment={experiment} />;
  const measuredHours =
    experiment?.decidedAt && experiment.publishedAt
      ? Math.max(
          0,
          Math.round(
            (new Date(experiment.decidedAt).getTime() -
              new Date(experiment.publishedAt).getTime()) /
              3_600_000,
          ),
        )
      : null;
  const meta = experiment
    ? [
        workspace?.name,
        experiment.publishedAt
          ? `Went out ${formatWhen(experiment.publishedAt)}`
          : 'Not scheduled',
        measuredHours !== null ? `measured ${measuredHours} h` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FlowPageHeader
        flowId={flowId}
        title={flow?.name ?? 'Flow'}
        chip={chip}
        meta={meta}
        view="results"
        action={
          <Button
            variant="outline"
            leadingIcon={Lightning}
            loading={start.isPending}
            onClick={() => void runAgain()}
          >
            Run again
          </Button>
        }
      />
      <div className="bg-bg-canvas min-h-0 flex-1 overflow-y-auto">
        <Stack gap={6} className="mx-auto w-full max-w-[1400px] p-8">
          {!experiments ? (
            <Skeleton className="h-40" shape="rectangle" />
          ) : experiments.length === 0 ? (
            <EmptyState
              icon={Flask}
              title="No experiments yet"
              description="Add a Split test and a Pick winner to this flow and run it. Results land here."
              actions={
                <Button asChild variant="outline">
                  <RouterLink to={`/flows/${flowId}`}>Open canvas</RouterLink>
                </Button>
              }
            />
          ) : (
            <>
              {experiments.length > 1 && (
                <Select
                  size="sm"
                  aria-label="Experiment"
                  value={experimentId}
                  onValueChange={setPicked}
                  className="w-80 self-start"
                >
                  {experiments.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {formatWhen(e.createdAt)} · {e.variantCount}{' '}
                      {NOUNS[e.variables[0] ?? '']?.[1] ?? 'variants'}
                      {e.winnerLabel ? ` · ${e.winnerLabel} won` : ''}
                    </SelectItem>
                  ))}
                </Select>
              )}
              {experiment ? (
                <Results workspaceId={workspaceId} experiment={experiment} />
              ) : (
                <Skeleton className="h-40" shape="rectangle" />
              )}
            </>
          )}
        </Stack>
      </div>
    </div>
  );
}

function StatusChip({ experiment }: { experiment: ExperimentDto }) {
  switch (experiment.status) {
    case 'DECIDED':
      return (
        <Badge tone="success" dot>
          Winner found
        </Badge>
      );
    case 'MEASURING':
      return (
        <Badge tone="info" dot>
          Measuring
        </Badge>
      );
    case 'NO_WINNER':
      return <Badge dot>No clear winner</Badge>;
  }
}

function Results({
  workspaceId,
  experiment,
}: {
  workspaceId: string;
  experiment: ExperimentDto;
}) {
  const metric = experiment.metric ?? 'watchTime';
  const variable = experiment.variables[0] ?? 'hook';
  return (
    <>
      <Banner workspaceId={workspaceId} experiment={experiment} metric={metric} />
      <div className="grid grid-cols-1 gap-6 laptop:grid-cols-[380px_1fr]">
        <Tested experiment={experiment} metric={metric} />
        <Bars experiment={experiment} metric={metric} variable={variable} />
      </div>
      <Stack gap={3}>
        <Inline gap={2}>
          <h2 className="text-heading-h3 text-text-primary">Variants</h2>
          <Badge>{experiment.variants.length}</Badge>
        </Inline>
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(180px,200px))] gap-5">
          {experiment.variants.map((variant) => (
            <li key={variant.id}>
              <VariantCard variant={variant} metric={metric} />
            </li>
          ))}
        </ul>
      </Stack>
    </>
  );
}

function Banner({
  workspaceId,
  experiment,
  metric,
}: {
  workspaceId: string;
  experiment: ExperimentDto;
  metric: Metric;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const promote = usePromoteWinner();
  const more = useMakeMoreLikeWinner();
  const [promoted, setPromoted] = useState(false);
  const winner = experiment.variants.find((v) => v.winner);
  const [singular, plural] = NOUNS[experiment.variables[0] ?? ''] ?? [
    'variant',
    'variants',
  ];

  if (experiment.status === 'MEASURING') {
    return (
      <Well tone="info" title="Measuring">
        The {experiment.variants.length} {plural} are out; the winner is picked after the
        Delay. Publishing isn’t connected yet, so enter each post’s numbers on the{' '}
        <RouterLink to="/calendar" className="underline">
          Calendar
        </RouterLink>
        .
      </Well>
    );
  }
  if (!winner) {
    return (
      <Well tone="default" title="No clear winner">
        {experiment.reason ?? 'The variants did about the same.'} Nothing was promoted,
        and the flow didn’t run again.
      </Well>
    );
  }

  const onPromote = async () => {
    try {
      await promote.mutateAsync({ id: workspaceId, experimentId: experiment.id });
    } catch {
      return;
    }
    setPromoted(true);
    toast.success(`${winner.label} saved to Media`);
  };
  const onMore = async () => {
    let flowId: string;
    try {
      flowId = (await more.mutateAsync({ id: workspaceId, experimentId: experiment.id }))
        .flowId;
    } catch {
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: getGetExperimentQueryKey(workspaceId, experiment.id),
    });
    toast.success(`Making 5 more like ${winner.label}`);
    void navigate(`/flows/${flowId}`);
  };

  const lift = experiment.lift;
  return (
    <section
      aria-label="Winner"
      className="bg-bg-inverse text-text-inverse flex flex-wrap items-center gap-5 rounded-2xl px-6 py-6"
    >
      <span className="bg-brand-primary text-text-on-brand flex size-12 shrink-0 items-center justify-center rounded-lg">
        <Trophy aria-hidden className="size-6" />
      </span>
      <Stack gap={1} className="min-w-0 flex-1">
        <h2 className="text-heading-h2">
          {winner.label} wins
          {lift !== null && lift > 0
            ? ` — +${Math.round(lift * 100)}% ${METRICS[metric].name}`
            : ''}
        </h2>
        <p className="text-body-md opacity-80">
          “{winner.value}” beat the other {experiment.variants.length - 1}{' '}
          {experiment.variants.length - 1 === 1 ? singular : plural}
          {HELD[experiment.variables[0] ?? '']
            ? ` on the same ${HELD[experiment.variables[0]!]!.join(', ')}`
            : ''}
          .
        </p>
      </Stack>
      <Inline gap={3} wrap>
        <Button
          variant="ghost"
          leadingIcon={BookmarkSimple}
          disabled={promoted}
          loading={promote.isPending}
          className="text-text-inverse hover:bg-bg-inverse-hover"
          onClick={() => void onPromote()}
        >
          {promoted ? 'Saved to Media' : 'Promote to library'}
        </Button>
        <Button
          leadingIcon={Sparkle}
          loading={more.isPending}
          onClick={() => void onMore()}
        >
          Make 5 more like this
        </Button>
      </Inline>
    </section>
  );
}

function Tested({ experiment, metric }: { experiment: ExperimentDto; metric: Metric }) {
  const variable = experiment.variables[0] ?? '';
  const rows: [string, ReactNode][] = [
    ['Variable', experiment.variables.map(capitalise).join(' × ')],
    ['Held constant', (HELD[variable] ?? []).map(capitalise).join(' · ') || '—'],
    [
      'Published',
      experiment.publishedAt
        ? `${experiment.accounts} ${experiment.accounts === 1 ? 'account' : 'accounts'} · same slot, ${formatWhen(experiment.publishedAt)}`
        : 'Not scheduled',
    ],
    ['Metric', capitalise(METRICS[metric].name)],
    [
      'Sample',
      experiment.minViews !== null
        ? `≥ ${compact(experiment.minViews)} views per variant`
        : '—',
    ],
    [
      'Confidence',
      experiment.confidence ? (
        <Badge tone={experiment.confidence === 'high' ? 'success' : 'warning'} dot>
          {capitalise(experiment.confidence)}
        </Badge>
      ) : (
        '—'
      ),
    ],
  ];
  return (
    <section
      aria-labelledby="tested"
      className="bg-bg-surface border-border-subtle rounded-2xl border p-6"
    >
      <Stack gap={4}>
        <h2 id="tested" className="text-heading-h4 text-text-primary">
          What was tested
        </h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-body-sm text-text-secondary">{label}</dt>
              <dd className="text-body-sm-strong text-text-primary text-right">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </Stack>
    </section>
  );
}

function Bars({
  experiment,
  metric,
  variable,
}: {
  experiment: ExperimentDto;
  metric: Metric;
  variable: string;
}) {
  const values = experiment.variants.map((v) => valueOf(metric, v));
  const max = Math.max(...values, 0);
  const title = `${METRICS[metric].by} by ${NOUNS[variable]?.[0] ?? 'variant'}`;
  return (
    <section
      aria-labelledby="bars"
      className="bg-bg-surface border-border-subtle rounded-2xl border p-6"
    >
      <Stack gap={4}>
        <Inline gap={2}>
          <h2 id="bars" className="text-heading-h4 text-text-primary flex-1">
            {title}
          </h2>
          {metric === 'watchTime' && (
            <span className="text-caption text-text-secondary">seconds</span>
          )}
        </Inline>
        <ul aria-label={title} className="flex flex-col gap-3">
          {experiment.variants.map((variant, i) => (
            <li
              key={variant.id}
              aria-label={`${variant.label}: ${METRICS[metric].format(values[i]!)}`}
              className="grid grid-cols-[88px_1fr_56px] items-center gap-3"
            >
              <span className="text-body-sm text-text-secondary truncate">
                {variant.label}
              </span>
              <span className="bg-bg-subtle h-7 overflow-hidden rounded-md">
                <span
                  className={cn(
                    'block h-full rounded-md',
                    variant.winner ? 'bg-brand-primary' : 'bg-bg-emphasis',
                  )}
                  style={{ width: max ? `${(values[i]! / max) * 100}%` : 0 }}
                />
              </span>
              <span
                className={cn(
                  'text-body-sm text-right tabular-nums',
                  variant.winner ? 'text-text-brand font-semibold' : 'text-text-primary',
                )}
              >
                {METRICS[metric].format(values[i]!)}
              </span>
            </li>
          ))}
        </ul>
      </Stack>
    </section>
  );
}

function VariantCard({
  variant,
  metric,
}: {
  variant: ExperimentVariantDto;
  metric: Metric;
}) {
  return (
    <article
      aria-label={variant.winner ? `${variant.label}, winner` : variant.label}
      className={cn(
        'bg-bg-surface flex flex-col gap-2 rounded-xl p-2.5',
        variant.winner ? 'border-border-brand border-2' : 'border-border-subtle border',
      )}
    >
      <div className="bg-bg-subtle relative aspect-[9/16] overflow-hidden rounded-lg">
        {variant.thumbnailUrl && (
          <img src={variant.thumbnailUrl} alt="" className="size-full object-cover" />
        )}
        <span
          className={cn(
            'text-caption absolute top-2 left-2 rounded-sm px-1.5 py-0.5 font-semibold',
            variant.winner
              ? 'bg-brand-primary text-text-on-brand'
              : 'bg-bg-inverse text-text-inverse',
          )}
        >
          {variant.winner ? `${variant.label} · Winner` : variant.label}
        </span>
      </div>
      <p className="text-body-sm text-text-primary truncate" title={variant.value}>
        “{variant.value}”
      </p>
      <Inline gap={2} align="between">
        <span className="text-caption text-text-secondary">
          {compact(variant.views)} views
        </span>
        <span className="text-caption text-text-secondary">
          {metric === 'watchTime' || metric === 'views'
            ? `${variant.avgWatchSeconds.toFixed(1)}s avg`
            : METRICS[metric].format(valueOf(metric, variant))}
        </span>
      </Inline>
    </article>
  );
}
